// ────────────────────────────────────────────────────────────────
// Participant Charts API client (Day 1 — Foundation Only)
// Thin wrapper around axios `api` for participant-chart endpoints.
// All routes are feature-flag gated server-side (charts_module_enabled).
// ────────────────────────────────────────────────────────────────

import api from '@/lib/api';

export async function listCharts(participantId, { status } = {}) {
  const params = {};
  if (status) params.status = status;
  const res = await api.get(`/participants/${participantId}/charts`, { params });
  return res.data?.data || [];
}

export async function createChart(participantId, payload) {
  const res = await api.post(`/participants/${participantId}/charts`, payload);
  return res.data?.data;
}

export async function getChart(participantId, chartId) {
  const res = await api.get(`/participants/${participantId}/charts/${chartId}`);
  return res.data?.data;
}

export async function updateChart(participantId, chartId, payload) {
  const res = await api.put(`/participants/${participantId}/charts/${chartId}`, payload);
  return res.data?.data;
}

export async function archiveChart(participantId, chartId) {
  const res = await api.delete(`/participants/${participantId}/charts/${chartId}`);
  return res.data?.data;
}

// Shared template registry. Actual options shown in the creation picker are
// filtered by the enabled feature flags on the frontend (see ParticipantChartCreateModal).
// Order here controls display order in the template picker.
export const AVAILABLE_TEMPLATES = [
  { code: 'bowel_chart', name: 'Bowel Chart', category: 'clinical', flag: 'bowel_chart_enabled' },
  { code: 'sleep_chart', name: 'Sleep Chart', category: 'clinical', flag: 'sleep_chart_enabled' },
  { code: 'pain_chart', name: 'Pain Chart', category: 'clinical', flag: 'pain_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'fluid_intake_chart', name: 'Fluid Intake Chart', category: 'clinical', flag: 'fluid_intake_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'behaviour_chart', name: 'Behaviour Chart (ABC)', category: 'clinical', flag: 'behaviour_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'wound_chart', name: 'Wound Chart', category: 'clinical', flag: 'wound_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  // Clinical Chart Catalog — Slice C (low-risk templates)
  { code: 'personal_hygiene_chart', name: 'Shower & Personal Hygiene Chart', category: 'adl', flag: 'personal_hygiene_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'vital_signs_chart', name: 'Vital Signs & Clinical Observations Chart', category: 'clinical', flag: 'vital_signs_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'blood_glucose_chart', name: 'Blood Glucose Monitoring Chart', category: 'clinical', flag: 'blood_glucose_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'exercise_therapy_chart', name: 'Exercise & Therapy Participation Chart', category: 'clinical', flag: 'exercise_therapy_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'community_access_chart', name: 'Community Access & Participation Chart', category: 'clinical', flag: 'community_access_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'weight_chart', name: 'Weight Monitoring Chart', category: 'clinical', flag: 'weight_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  // Clinical Chart Catalog — Slice D Wave 1 (non-photo high-risk)
  { code: 'neuro_obs_chart', name: 'Neurological Observation Chart', category: 'clinical', flag: 'neuro_obs_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'mood_mental_health_chart', name: 'Mood & Mental Health Chart', category: 'clinical', flag: 'mood_mental_health_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'seizure_chart', name: 'Seizure Chart', category: 'clinical', flag: 'seizure_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'post_seizure_chart', name: 'Post-Seizure Recovery Chart', category: 'clinical', flag: 'post_seizure_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'restrictive_practice_chart', name: 'Restrictive Practice Chart', category: 'clinical', flag: 'restrictive_practice_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  // Clinical Chart Catalog — Slice D Wave 2 (photo-dependent high-risk)
  { code: 'post_fall_chart', name: 'Post-Fall Observation Chart', category: 'clinical', flag: 'post_fall_chart_enabled', requires: ['multi_chart_templates_enabled'] },
  { code: 'pressure_injury_chart', name: 'Pressure Injury / Skin Integrity Chart', category: 'clinical', flag: 'pressure_injury_chart_enabled', requires: ['multi_chart_templates_enabled'] },
];

// Templates that use the dynamic field engine (vs bespoke bowel/sleep UI).
export const DYNAMIC_TEMPLATE_CODES = new Set([
  'pain_chart', 'fluid_intake_chart', 'behaviour_chart', 'wound_chart',
  'personal_hygiene_chart', 'vital_signs_chart', 'blood_glucose_chart',
  'exercise_therapy_chart', 'community_access_chart', 'weight_chart',
  'neuro_obs_chart', 'mood_mental_health_chart', 'seizure_chart',
  'post_seizure_chart', 'restrictive_practice_chart',
  'post_fall_chart', 'pressure_injury_chart',
]);

export const MONITORING_FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'per_shift', label: 'Per shift' },
  { value: 'twice_daily', label: 'Twice daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'as_needed', label: 'As needed' },
  { value: 'custom', label: 'Custom' },
];
